import { type ReactNode } from "react";
import { Link, NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LayoutGrid, Upload, ArrowLeft, Users, FileClock, Sparkles } from "lucide-react";

/**
 * AdminPortalShell — dark-sidebar layout for `/admin/*` routes.
 *
 * Replaces the prior `AdminLayout` (which lives on inside the same
 * directory until a follow-up cleanup ticket removes it) for the three
 * existing admin pages: Question list, Bulk import, Editor. Adopted
 * from the 2026-05-04 Replit graduation under a stripped scope: only
 * Questions + Bulk Import nav items render, since the routes for the
 * other admin sections (Translations, Country Rules, Team) graduate as
 * follow-up tickets RB-G6 / RB-G7 / RB-G8.
 *
 * App.tsx suppresses `<TopNav />` on `/admin/*` so this shell owns the
 * full chrome. The bottom of the sidebar is a "Back to dashboard" link
 * — admin users return to `/dashboard` to access TopNav's UserMenu for
 * logout. A future ticket can add an in-shell user widget if desired.
 */
interface AdminPortalShellProps {
  title: string;
  subtitle?: string;
  action?: ReactNode;
  children: ReactNode;
  testId?: string;
}

export default function AdminPortalShell({
  title,
  subtitle,
  action,
  children,
  testId,
}: AdminPortalShellProps) {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen flex bg-[#18181b]" data-testid={testId}>
      {/* LEFT SIDEBAR */}
      <aside
        aria-label={t("admin.portal.sidebarLabel")}
        className="w-56 shrink-0 flex flex-col bg-[#18181b] h-screen sticky top-0"
      >
        {/* TOP — brand */}
        <div className="p-5 flex items-center gap-2">
          <div
            aria-hidden="true"
            className="flex h-8 w-8 items-center justify-center rounded bg-white text-[#18181b] font-bold"
          >
            RB
          </div>
          <div className="flex flex-col">
            <span className="text-white font-semibold leading-none">
              RoadBridge
            </span>
            <span className="text-slate-400 text-[10px] uppercase font-bold tracking-wider mt-0.5">
              {t("admin.portal.brand")}
            </span>
          </div>
        </div>

        {/* MAIN NAV */}
        <nav
          aria-label={t("admin.portal.navLabel")}
          className="flex flex-col gap-1 px-3 py-4 flex-1"
        >
          <NavLink
            to="/admin/questions"
            end
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors no-underline ${
                isActive
                  ? "bg-blue-600 text-white shadow-sm"
                  : "text-slate-400 hover:bg-white/5 hover:text-white"
              }`
            }
          >
            <LayoutGrid aria-hidden="true" className="w-4 h-4" />
            {t("admin.portal.nav.questions")}
          </NavLink>
          <NavLink
            to="/admin/questions/import"
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors no-underline ${
                isActive
                  ? "bg-blue-600 text-white shadow-sm"
                  : "text-slate-400 hover:bg-white/5 hover:text-white"
              }`
            }
          >
            <Upload aria-hidden="true" className="w-4 h-4" />
            {t("admin.portal.nav.bulkImport")}
          </NavLink>
          <NavLink
            to="/admin/users"
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors no-underline ${
                isActive
                  ? "bg-blue-600 text-white shadow-sm"
                  : "text-slate-400 hover:bg-white/5 hover:text-white"
              }`
            }
          >
            <Users aria-hidden="true" className="w-4 h-4" />
            Users
          </NavLink>
          <NavLink
            to="/admin/audit"
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors no-underline ${
                isActive
                  ? "bg-blue-600 text-white shadow-sm"
                  : "text-slate-400 hover:bg-white/5 hover:text-white"
              }`
            }
          >
            <FileClock aria-hidden="true" className="w-4 h-4" />
            Audit Log
          </NavLink>
          <NavLink
            to="/admin/_showcase"
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2 text-xs font-medium transition-colors no-underline ${
                isActive
                  ? "bg-blue-600 text-white shadow-sm"
                  : "text-slate-500 hover:bg-white/5 hover:text-white"
              }`
            }
          >
            <Sparkles aria-hidden="true" className="w-3.5 h-3.5" />
            Design showcase
          </NavLink>
        </nav>

        {/* BOTTOM — back-to-dashboard link */}
        <div className="p-4 mt-auto border-t border-white/10">
          <Link
            to="/dashboard"
            data-testid="admin-portal-back-to-dashboard"
            className="flex items-center gap-2 text-xs font-medium text-slate-400 hover:text-white transition-colors w-full px-2 py-1.5 rounded-md hover:bg-white/5 no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            <ArrowLeft aria-hidden="true" className="w-3.5 h-3.5" />
            {t("admin.portal.backToDashboard")}
          </Link>
        </div>
      </aside>

      {/* RIGHT MAIN AREA */}
      <div className="flex-1 flex flex-col bg-slate-50 min-h-0 min-w-0">
        <header className="h-14 shrink-0 border-b border-slate-200 bg-white flex items-center justify-between px-6">
          <h1 className="text-slate-900 font-semibold">{title}</h1>
          {action && <div>{action}</div>}
        </header>
        <div className="flex-1 flex flex-col p-6 overflow-y-auto min-h-0">
          {subtitle && (
            <p className="text-sm text-slate-500 mb-6">{subtitle}</p>
          )}
          <div className="flex-1 flex flex-col min-h-0">{children}</div>
        </div>
      </div>
    </div>
  );
}
